import type { ConsignmentLineItem } from '../model/types';
import { portionUnits, type LineTotals, type PalletConfig, type PalletPortion } from './pallet-config';

/** `"200 Each"` → `{ quantity: 200, unit: 'Each' }`; nothing or another shape → `{ quantity: 0, unit: '' }` */
export function parseInventoryUnits(inventoryUnits: string | null | undefined): { quantity: number; unit: string } {
  const match = inventoryUnits?.match(/^([\d.]+)\s+(.+)$/);
  return match ? { quantity: parseFloat(match[1]), unit: match[2] } : { quantity: 0, unit: '' };
}

/**
 * The number in a backend display value, which is US-formatted with a unit:
 * `"1,234.5 kg"` → 1234.5. Nothing or no number → 0.
 */
export function parseNumericValue(value: string | number | null | undefined): number {
  if (typeof value === 'number') return value;
  const parsed = parseFloat((value ?? '').replace(/,/g, ''));
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Inventory units of each whole line */
export const lineTotals = (lines: ConsignmentLineItem[]): LineTotals =>
  Object.fromEntries(lines.map((line) => [line.id, parseInventoryUnits(line.inventoryUnits).quantity]));

/**
 * Inventory units of the portions together, and their common unit type (`null`
 * when the lines have different ones). Portions of unknown lines count nothing.
 */
export function summarizePortions(
  portions: PalletPortion[],
  config: PalletConfig,
  lines: ConsignmentLineItem[]
): { unit: string | null; totalUnits: number } {
  const lineById = new Map(lines.map((line) => [line.id, line]));
  const units = new Set<string>();
  let totalUnits = 0;

  for (const portion of portions) {
    const { quantity, unit } = parseInventoryUnits(lineById.get(portion.lineId)?.inventoryUnits);
    const portionQuantity = portionUnits(config, portion, quantity);
    if (portionQuantity > 0 && unit) {
      totalUnits += portionQuantity;
      units.add(unit);
    }
  }

  return { unit: units.size === 1 ? [...units][0] : null, totalUnits };
}
